/**
 * Work System - API Client
 * Tum /api/work/* endpoint cagrisi
 */

import axios from 'axios'
import { API_URL } from '../config/api'
import type {
    CreateWorkTaskRequest,
    CreateWorkTaskResponse,
    WorkCategoryInfo,
    WorkDashboard,
    WorkDownloadResponse,
    WorkEstimate,
    WorkResultResponse,
    WorkStats,
    WorkTask,
} from '../types/work'

// ─── Axios Instance ───────────────────────────────────────────────────────────

const workApi = axios.create({
  baseURL: `${API_URL}/api/work`,
})

// Auth header - her istekte JWT token ekle
workApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('session_token')
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ─── Categories ───────────────────────────────────────────────────────────────

export async function getWorkCategories(): Promise<WorkCategoryInfo[]> {
  const res = await workApi.get<{ categories: WorkCategoryInfo[] }>('/categories')
  return res.data.categories
}

export async function getWorkCategory(categoryId: string): Promise<WorkCategoryInfo> {
  const res = await workApi.get<{ category: WorkCategoryInfo }>(`/categories/${categoryId}`)
  return res.data.category
}

// ─── Estimate ─────────────────────────────────────────────────────────────────

export interface EstimateParams {
  subcategory: string
  input_length?: number
  duration_seconds?: number
  image_count?: number
}

export async function getWorkEstimate(params: EstimateParams): Promise<WorkEstimate> {
  const res = await workApi.get<WorkEstimate>('/estimate', { params })
  return res.data
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

export async function createWorkTask(data: CreateWorkTaskRequest): Promise<CreateWorkTaskResponse> {
  const res = await workApi.post<CreateWorkTaskResponse>('/tasks', data)
  return res.data
}

export interface GetWorkTasksParams {
  status?: string
  category?: string
  limit?: number
  offset?: number
}

export async function getWorkTasks(params?: GetWorkTasksParams): Promise<WorkTask[]> {
  const res = await workApi.get<{ tasks: WorkTask[] }>('/tasks', { params })
  return res.data.tasks
}

export async function getPublicWorkTasks(params?: GetWorkTasksParams): Promise<WorkTask[]> {
  const res = await workApi.get<{ tasks: WorkTask[] }>('/public/tasks', { params })
  return res.data.tasks
}

export async function getWorkTask(taskId: number): Promise<WorkTask> {
  const res = await workApi.get<{ task: WorkTask }>(`/tasks/${taskId}`)
  return res.data.task
}

export async function cancelWorkTask(taskId: number): Promise<{ success: boolean; flz_refunded: number }> {
  const res = await workApi.delete<{ success: boolean; flz_refunded: number }>(`/tasks/${taskId}`)
  return res.data
}

// ─── File Upload ──────────────────────────────────────────────────────────────

export async function uploadWorkFile(taskId: number, file: File): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  await workApi.post(`/tasks/${taskId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// ─── Result & Download ────────────────────────────────────────────────────────

export async function getWorkResult(taskId: number): Promise<WorkResultResponse> {
  const res = await workApi.get<WorkResultResponse>(`/tasks/${taskId}/result`)
  return res.data
}

export async function getWorkDownloadUrl(taskId: number): Promise<WorkDownloadResponse> {
  const res = await workApi.get<WorkDownloadResponse>(`/tasks/${taskId}/download`)
  return res.data
}

// ─── Stats & History ──────────────────────────────────────────────────────────

export async function getWorkStats(): Promise<WorkStats> {
  const res = await workApi.get<WorkStats>('/stats')
  return res.data
}

export async function getWorkHistory(limit?: number): Promise<WorkTask[]> {
  const res = await workApi.get<{ history: WorkTask[] }>('/history', {
    params: limit ? { limit } : undefined,
  })
  return res.data.history
}

export async function getWorkDashboard(): Promise<WorkDashboard> {
  const res = await workApi.get<WorkDashboard>('/dashboard')
  return res.data
}
